import React, { useContext, useEffect, useMemo, useState } from "react";
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Grid,
    Input,
    Text,
    Textarea,
    useColorModeValue,
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import { useTranslation } from "react-i18next";
import { ROUTES } from "../../../constants/routes-path";
import { useNavigate } from "react-router-dom";
import { useCustomToast } from "../../../components/CustomToast/CustomToast";
import { agentScheduleService } from "../../../services/agentScheduleService";
import DynamicTable from "../../../components/DynamicTable/DynamicTable";
import { agentsService } from "../../../services/agentsService";
import { AuthContext } from "../../../contexts/AuthContext";
import { checkErrors, hasError } from "utils/forms-utils";
import { fromServerDate, toServerDate } from "../../../utils/traffic-violation-utils";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import moment from "moment";
import "moment/locale/pt-br";
import DateTime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import { LOCALE_PT_BR, DATE_FORMAT_ISO } from "constants/date-time";
import { DATE_FORMAT } from "constants/date-time";

function AgentScheduleNew({ data }) {
    const textColor = useColorModeValue("gray.700", "white");
    const bgTextarea = useColorModeValue("white", "navy.900");
    const placeholderColor = useColorModeValue("gray.300", "gray.400");
    const [isSaving, setIsSaving] = useState(false);
    const [cellErrors, setCellErrors] = useState({});

    const { t } = useTranslation();
    const navigate = useNavigate();
    const toast = useCustomToast();

    const { getUserData } = useContext(AuthContext);
    const user = getUserData();

    const ids = useMemo(() => ({
        name: "name",
        initialDate: "initialDate",
        endDate: "endDate",
        observations: "observations",
        timeSlots: "timeSlots",
    }), []);

    const validateTimeSlots = (rows) => {
        if (!Array.isArray(rows) || rows.length === 0) return "system.messages.errors.required-field";
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            if (!row.id_agente) return "system.messages.errors.required-field";
            if (!row.hora_inicio) return "system.messages.errors.required-field";
            if (!row.hora_fim) return "system.messages.errors.required-field";
        }
        return null;
    };

    const validateTimeSlotsDetailed = (rows) => {
        const errors = {};
        if (!Array.isArray(rows)) return errors;

        rows.forEach((row, rowIndex) => {
            const rowErrors = {};
            if (!row.id_agente) rowErrors.id_agente = "system.messages.errors.required-field";
            if (!row.hora_inicio) rowErrors.hora_inicio = "system.messages.errors.required-field";
            if (!row.hora_fim) rowErrors.hora_fim = "system.messages.errors.required-field";
            if (Object.keys(rowErrors).length > 0) {
                errors[rowIndex] = rowErrors;
            }
        });

        return errors;
    };

    const initFormData = (values) => ({
        [ids.name]: {
            value: values?.name || "",
            validations: [
                { required: true },
                {
                    callback: ({ value }) =>
                        value.length > 255 ? t("system.messages.errors.max-length", { max: 255 }) : null,
                }
            ],
        },
        [ids.initialDate]: {
            value: fromServerDate(values?.initialDate) || "",
            validations: [{ required: true }],
        },
        [ids.endDate]: {
            value: fromServerDate(values?.endDate) || "",
            validations: [],
        },
        [ids.observations]: {
            value: values?.observations || "",
            validations: [],
        },
        [ids.timeSlots]: {
            value: values?.timeSlots || [],
            validations: [
                { callback: ({ value }) => validateTimeSlots(value) },
            ],
        }
    });

    const [formData, setFormData] = useState(undefined);
    const [inputError, setInputError] = useState({});

    const isFormInvalid = formData && inputError
        ? hasError({ formData, inputError })
        : true;

    useEffect(() => {
        setFormData(initFormData(data));
    }, [data]);

    const handleInputChange = (e) => {
        const id = e.target.name;
        const value = e.target.value;

        const newFormData = {
            ...formData,
            [id]: {
                ...formData[id],
                value,
            },
        };

        setFormData(newFormData);
        setInputError(checkErrors({ formData: newFormData, inputError, name: id }));
    };

    const handleInitialDateChange = (value) => {
        const dateValue = moment(value).isValid()
            ? moment(value).format(DATE_FORMAT_ISO)
            : "";

        const newFormData = {
            ...formData,
            [ids.initialDate]: {
                ...formData[ids.initialDate],
                value: dateValue,
            },
        };

        setFormData(newFormData);
        setInputError((prev) =>
            checkErrors({
                formData: newFormData,
                inputError: prev,
                name: ids.initialDate,
            })
        );
    };

    const handleEndDateChange = (value) => {
        const dateValue = moment(value).isValid()
            ? moment(value).format(DATE_FORMAT_ISO)
            : "";

        const newFormData = {
            ...formData,
            [ids.endDate]: {
                ...formData[ids.endDate],
                value: dateValue,
            },
        };

        setFormData(newFormData);
        setInputError((prev) =>
            checkErrors({
                formData: newFormData,
                inputError: prev,
                name: ids.endDate,
            })
        );
    };

    const handleTableChange = (event) => {
        const name = event.target.name;
        const value = event.target.value;

        const newFormData = {
            ...formData,
            [name]: {
                ...formData[name],
                value,
            },
        };

        setFormData(newFormData);
        setInputError(checkErrors({ formData: newFormData, inputError, name }));
        setCellErrors(validateTimeSlotsDetailed(value));
    };

    const [agentData, setAgentData] = useState([]);

    useEffect(() => {
        const loadData = async () => {
            try {
                const resAgent = await agentsService.list({ pageSize: 999 });
                setAgentData(resAgent.content);
            } catch (error) {
                console.error(t("system.error.api.loading-data"), error);
            }
        };

        void loadData();
    }, []);

    useEffect(() => {
        const rows = formData?.[ids.timeSlots]?.value || [];
        setCellErrors(validateTimeSlotsDetailed(rows));
    }, [formData?.[ids.timeSlots]?.value]);

    const daysOfWeek = [
        { value: "MONDAY", label: "Segunda-feira" },
        { value: "TUESDAY", label: "Terça-feira" },
        { value: "WEDNESDAY", label: "Quarta-feira" },
        { value: "THURSDAY", label: "Quinta-feira" },
        { value: "FRIDAY", label: "Sexta-feira" },
        { value: "SATURDAY", label: "Sábado" },
        { value: "SUNDAY", label: "Domingo" },
    ];

    const timeSlotsColumns = useMemo(() => [
        {
            key: "id_agente",
            label: t("settings.agent-schedule.form.fields.time-slots.dynamic-columns.agent.label"),
            type: "SearchSelect",
            props: {
                items: agentData,
                id: "agent",
                displayField: ["codigo", "nome"],
                valueField: "id",
                placeholder: t("settings.agent-schedule.form.fields.time-slots.dynamic-columns.agent.placeholder"),
                required: true,
            }
        },
        {
            key: "dia_semana_inicio",
            label: t("settings.agent-schedule.form.fields.time-slots.dynamic-columns.initial-day.label"),
            type: "select",
            options: daysOfWeek,
            defaultValue: "MONDAY"
        },
        {
            key: "dia_semana_fim",
            label: t("settings.agent-schedule.form.fields.time-slots.dynamic-columns.end-day.label"),
            type: "select",
            options: daysOfWeek,
            defaultValue: "TUESDAY"
        },
        {
            key: "hora_inicio",
            label: t("settings.agent-schedule.form.fields.time-slots.dynamic-columns.start-time.label"),
            type: "time",
            defaultValue: "09:00",
            props: { required: true }
        },
        {
            key: "hora_fim",
            label: t("settings.agent-schedule.form.fields.time-slots.dynamic-columns.end-time.label"),
            type: "time",
            defaultValue: "17:00",
            props: { required: true }
        }
    ], [agentData]);

    const dayMapReverse = {
        MONDAY: 1,
        TUESDAY: 2,
        WEDNESDAY: 3,
        THURSDAY: 4,
        FRIDAY: 5,
        SATURDAY: 6,
        SUNDAY: 7
    };

    const toDTO = () => ({
        id: data?.id,
        nome: formData[ids.name].value,
        data_inicial: toServerDate(formData[ids.initialDate].value),
        data_final: toServerDate(formData[ids.endDate].value),
        observacoes: formData[ids.observations].value,
        id_usuario_criacao: user.id,
        data_criacao: new Date(),
        workhours: formData[ids.timeSlots].value.map(slot => ({
            ...slot,
            dia_semana_inicio: dayMapReverse[slot.dia_semana_inicio],
            dia_semana_fim: dayMapReverse[slot.dia_semana_fim],
        })),
    });

    const onSave = async (e) => {
        e.preventDefault();
        if (isFormInvalid || isSaving) return;
        setIsSaving(true);

        try {
            if (data) {
                await agentScheduleService.update(data.id, toDTO());
            } else {
                await agentScheduleService.create(toDTO());
            }

            navigate(ROUTES.agentsSchedulesPath);

            toast({
                title: t(`settings.agent-schedule.messages.success.${data ? 'edit' : 'create'}`),
                status: "success",
            });

        } catch (error) {
            toast({
                title: t("system.notifications.error"),
                description: t(error.message),
                status: "error",
            });
        } finally {
            setIsSaving(false);
        }
    };

    return formData && (<Flex direction="column" minH="89vh" align="center" pt={{sm: "120px", md: "75px"}}>
            <Card>
                <ListPageCardHeader
                    title={t(`settings.agent-schedule.form.title${data ? '-edit' : ''}`)}
                    subtitle={t(`settings.agent-schedule.form.subtitle${data ? '-edit' : ''}`)}
                />
                <CardBody px="22px">
                    <Flex direction="column" w="100%">
                        <Grid gap="24px">
                            <FormControl isRequired isInvalid={!!inputError[ids.name]}>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t('settings.agent-schedule.form.fields.name.label')}
                                </FormLabel>
                                <Input
                                    variant="main"
                                    name={ids.name}
                                    value={formData[ids.name].value}
                                    onChange={handleInputChange}
                                    placeholder={t('settings.agent-schedule.form.fields.name.placeholder')}
                                    fontSize="sm"
                                />
                                <FormErrorMessage>{t(inputError[ids.name])}</FormErrorMessage>
                            </FormControl>
                        </Grid>
                        <Grid
                            templateColumns={{sm: "1fr", md: "repeat(2, 1fr)"}}
                            gap="24px"
                            mt="24px"
                        >
                            <FormControl isRequired isInvalid={!!inputError[ids.initialDate]}>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t('settings.agent-schedule.form.fields.initial-date.label')}
                                </FormLabel>
                                <DateTime
                                    dateFormat={DATE_FORMAT}
                                    timeFormat={false}
                                    locale={LOCALE_PT_BR}
                                    value={
                                        formData[ids.initialDate].value
                                            ? moment(formData[ids.initialDate].value)
                                            : ""
                                    }
                                    renderInput={(props) => (
                                        <Input
                                            {...props}
                                            variant="main"
                                            fontSize="sm"
                                            placeholder={t("settings.agent-schedule.form.fields.initial-date.placeholder")}
                                        />
                                    )}
                                    onChange={handleInitialDateChange}
                                />
                                <FormErrorMessage>{t(inputError[ids.initialDate])}</FormErrorMessage>
                            </FormControl>
                            <FormControl isInvalid={!!inputError[ids.endDate]}>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t('settings.agent-schedule.form.fields.end-date.label')}
                                </FormLabel>
                                <DateTime
                                    dateFormat={DATE_FORMAT}
                                    timeFormat={false}
                                    locale={LOCALE_PT_BR}
                                    value={
                                        formData[ids.endDate].value
                                            ? moment(formData[ids.endDate].value)
                                            : ""
                                    }
                                    renderInput={(props) => (
                                        <Input
                                            {...props}
                                            variant="main"
                                            fontSize="sm"
                                            placeholder={t("settings.agent-schedule.form.fields.end-date.placeholder")}
                                        />
                                    )}
                                    onChange={handleEndDateChange}
                                />
                                <FormErrorMessage>{t(inputError[ids.endDate])}</FormErrorMessage>
                            </FormControl>
                        </Grid>
                        <Grid mt="24px" gap="24px">
                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t('settings.agent-schedule.form.fields.observations.label')}
                                </FormLabel>
                                <Textarea
                                    minH="120px"
                                    name={ids.observations}
                                    value={formData[ids.observations].value}
                                    onChange={handleInputChange}
                                    bg={bgTextarea}
                                    _placeholder={{ color: placeholderColor }}
                                    placeholder={t('settings.agent-schedule.form.fields.observations.placeholder')}
                                    fontSize="sm"
                                />
                            </FormControl>
                        </Grid>
                        <Grid gap="24px" mt="24px" mb="24px">
                            <FormControl isRequired isInvalid={!!inputError[ids.timeSlots]}>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t('settings.agent-schedule.form.fields.time-slots.label')}
                                </FormLabel>
                                <DynamicTable
                                  id={ids.timeSlots}
                                  columns={timeSlotsColumns}
                                  value={formData[ids.timeSlots].value}
                                  onChange={handleTableChange}
                                  isInvalid={!!inputError[ids.timeSlots]}
                                  addRowLabel={t('settings.agent-schedule.form.buttons.add-time-slot')}
                                  emptyMessage={t('settings.agent-schedule.form.fields.time-slots.empty-message')}
                                  errorMessage={inputError[ids.timeSlots] ? t(inputError[ids.timeSlots]) : null}
                                  isRequired
                                  cellErrors={cellErrors}
                                />
                            </FormControl>
                        </Grid>
                        <Flex justify="space-between">
                            <Button
                                w="125px"
                                variant="no-effects"
                                bg="gray.100"
                                color="gray.700"
                                fontSize="sm"
                                fontWeight="bold"
                                onClick={() => navigate(ROUTES.agentsSchedulesPath)}
                            >
                                {t(`settings.breathalyzer.form.buttons.${data ? 'cancel' : 'back'}`)}
                            </Button>
                            <Button
                                w="125px"
                                variant="dark"
                                fontSize="sm"
                                type="submit"
                                onClick={onSave}
                                disabled={isFormInvalid}
                            >
                                {t(`settings.breathalyzer.form.buttons.${data ? 'update' : 'save'}`)}
                            </Button>
                        </Flex>
                    </Flex>
                </CardBody>
            </Card>
        </Flex>
    );
}

export default AgentScheduleNew;
